/**
 * AutomationSettings Component
 *
 * Automation tab content — an overview of the lifecycle diagrams that opens the
 * settings of a single step; steps without settings show a read-only panel.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChatNudgeSignal } from '@insio/shared-types';
import { useNudgeSignal } from '@/features/ai-chat/nudges/use-nudge-signal';
import { Button } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { FeatureFlagKey, useFeatureFlag } from '@/features/feature-flags';
import { AutofillSettingsSection } from './AutofillSettingsSection';
import { ExtractionSettingsSection } from './ExtractionSettingsSection';
import { InvoiceApprovalSettingsSection } from './InvoiceApprovalSettingsSection';
import { InvoiceClosureSettingsSection } from './InvoiceClosureSettingsSection';
import { InvoiceMatchConfirmationSettingsSection } from './InvoiceMatchConfirmationSettingsSection';
import { InvoiceValidationSettingsSection } from './InvoiceValidationSettingsSection';
import { PurchaseOrderApprovalSettingsSection } from './PurchaseOrderApprovalSettingsSection';
import { PurchaseOrderClosureSettingsSection } from './PurchaseOrderClosureSettingsSection';
import { PurchaseOrderValidationSection } from './PurchaseOrderValidationSection';
import { RequisitionApprovalSettingsSection } from './RequisitionApprovalSettingsSection';
import { RequisitionClosureSettingsSection } from './RequisitionClosureSettingsSection';
import { RequisitionOrderingSettingsSection } from './RequisitionOrderingSettingsSection';
import { RequisitionValidationSection } from './RequisitionValidationSection';
import { LifecycleStepInfo } from './LifecycleStepInfo';
import { DocumentLifecycle } from './DocumentLifecycle';
import { useCanChangeAutomationSettings } from './use-can-change-automation-settings';
import {
  LIFECYCLE_STEP_KEYS,
  isProcurementStep,
  lifecycleStepFromParam,
  type LifecycleDocument,
  type LifecycleStepKey,
  type LifecycleStepRef
} from './types';

export interface AutomationSettingsProps {
  /** Opens the integrations tab; undefined hides the shortcut on the Export step. */
  onOpenIntegrations?: () => void;
}

const DOCUMENTS: readonly LifecycleDocument[] = ['invoice', 'requisition', 'purchaseOrder'];
/** The requisition and purchase order lifecycles belong to P2P and go with its flag. */
const DOCUMENTS_WITHOUT_PROCUREMENT: readonly LifecycleDocument[] = ['invoice'];
/** How long the page keeps its height for a step whose content does not resize after opening. */
const HOLD_HEIGHT_MS = 1000;

/** The nearest ancestor that scrolls vertically; null when none does. */
function scrollContainerOf(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
}

export function AutomationSettings({ onOpenIntegrations }: AutomationSettingsProps) {
  const t = useFeatureTranslation('systemSettings.automation.lifecycle');
  const isProcurementEnabled = useFeatureFlag(FeatureFlagKey.PROCUREMENT);
  // Whoever only reads the rules sees every setting locked; one line says why.
  const canChange = useCanChangeAutomationSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const signalNudge = useNudgeSignal();
  // Opening the tab is a good moment to offer guided set-up; the server decides if it applies.
  useEffect(() => {
    signalNudge(ChatNudgeSignal.AUTOMATION_SETTINGS_OPENED);
    // Once per visit to the tab, not on every chat update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The tab starts on the overview, unless a link named a step (`?step=purchaseOrder.closure`).
  const [pickedStep, setPickedStep] = useState<LifecycleStepRef | null>(() =>
    lifecycleStepFromParam(searchParams.get('step'))
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const stepRef = useRef<HTMLDivElement>(null);
  // The height kept while a newly opened step loads, so the page does not shrink and jump up.
  const [heldHeight, setHeldHeight] = useState<number | undefined>(undefined);
  // Set when the user picks another step; a step named in the URL opens where the page starts.
  const scrollToStep = useRef(false);

  // A linked step opens once: leaving it drops it from the URL, so a reload does not reopen it.
  const pickStep = (step: LifecycleStepRef | null) => {
    const isOtherStep =
      step !== null && (step.document !== pickedStep?.document || step.step !== pickedStep.step);
    if (isOtherStep) {
      setHeldHeight(rootRef.current?.offsetHeight);
      scrollToStep.current = true;
    }
    setPickedStep(step);
    if (!searchParams.has('step')) return;
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete('step');
        return next;
      },
      { replace: true }
    );
  };

  const documents = isProcurementEnabled ? DOCUMENTS : DOCUMENTS_WITHOUT_PROCUREMENT;
  // A step that is no longer shown (flag turned off meanwhile) is not open.
  const openStep =
    pickedStep && (isProcurementEnabled || !isProcurementStep(pickedStep)) ? pickedStep : null;

  const openStepKey = openStep ? `${openStep.document}-${openStep.step}` : null;

  // A step opens with its settings at the top of the screen, when the page is taller than that.
  useLayoutEffect(() => {
    if (!scrollToStep.current) return;
    scrollToStep.current = false;
    const step = stepRef.current;
    const box = step && scrollContainerOf(step);
    if (!step || !box || box.scrollHeight <= box.clientHeight) return;
    box.scrollTop += step.getBoundingClientRect().top - box.getBoundingClientRect().top;
  }, [openStepKey]);

  // The held height goes once the step's content resizes (its loader is replaced), or soon anyway.
  useEffect(() => {
    if (heldHeight === undefined) return;
    const content = stepRef.current?.firstElementChild;
    const release = () => setHeldHeight(undefined);
    const timer = setTimeout(release, HOLD_HEIGHT_MS);
    if (!content || typeof ResizeObserver === 'undefined') return () => clearTimeout(timer);
    let observed = false;
    const observer = new ResizeObserver(() => {
      // The first call reports the size at observe time, not a change.
      if (observed) release();
      observed = true;
    });
    observer.observe(content);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [heldHeight, openStepKey]);

  // The longest lifecycle spans the width; the others' cards are as wide as its.
  const slots =
    documents.length > 1
      ? Math.max(...documents.map(document => LIFECYCLE_STEP_KEYS[document].length))
      : undefined;

  const lifecycle = (document: LifecycleDocument, leading?: ReactNode) => (
    <DocumentLifecycle
      key={document}
      document={document}
      openStep={openStep?.document === document ? openStep.step : null}
      slots={slots}
      leading={leading}
      onSelectStep={(step: LifecycleStepKey) => pickStep({ document, step })}
    />
  );

  const readOnlyHint = !canChange && (
    <p className='text-xs text-foreground-muted' data-testid='automation-read-only-hint'>
      {t('readOnly')}
    </p>
  );

  if (!openStep) {
    // Overview — every document's diagram, stacked.
    return (
      <div ref={rootRef} className='space-y-xl'>
        {readOnlyHint}
        {documents.map(document => lifecycle(document))}
      </div>
    );
  }

  const renderStep = () => {
    if (openStep.document === 'purchaseOrder') {
      switch (openStep.step) {
        case 'validation':
          return <PurchaseOrderValidationSection />;
        case 'approval':
          return <PurchaseOrderApprovalSettingsSection onOpenStep={pickStep} />;
        case 'closure':
          return <PurchaseOrderClosureSettingsSection />;
        default:
          return <LifecycleStepInfo document='purchaseOrder' stepKey={openStep.step} />;
      }
    }
    if (openStep.document === 'requisition') {
      switch (openStep.step) {
        case 'validation':
          return <RequisitionValidationSection />;
        case 'approval':
          return <RequisitionApprovalSettingsSection onOpenStep={pickStep} />;
        case 'ordering':
          return <RequisitionOrderingSettingsSection />;
        case 'closure':
          return <RequisitionClosureSettingsSection />;
        default:
          return <LifecycleStepInfo document='requisition' stepKey={openStep.step} />;
      }
    }
    switch (openStep.step) {
      case 'extraction':
        return <ExtractionSettingsSection />;
      case 'autofill':
        return <AutofillSettingsSection />;
      case 'matching':
        return <InvoiceMatchConfirmationSettingsSection />;
      case 'validation':
        return <InvoiceValidationSettingsSection />;
      case 'approval':
        return <InvoiceApprovalSettingsSection onOpenStep={pickStep} />;
      case 'closure':
        return <InvoiceClosureSettingsSection />;
      case 'export':
        return (
          <LifecycleStepInfo
            document='invoice'
            stepKey='export'
            onOpenIntegrations={onOpenIntegrations}
          />
        );
      default:
        return <LifecycleStepInfo document='invoice' stepKey={openStep.step} />;
    }
  };

  return (
    <div ref={rootRef} className='space-y-lg' style={{ minHeight: heldHeight }}>
      {readOnlyHint}
      <div className='space-y-md border-b border-border-base pb-lg'>
        {lifecycle(
          openStep.document,
          <Button
            variant='ghost'
            size='md'
            icon='arrow-left'
            // Round and as tall as the rail's nodes, so it reads as part of the diagram.
            className='w-9 flex-none rounded-full px-0'
            onClick={() => pickStep(null)}
            aria-label={t('backToOverview')}
            title={t('backToOverview')}
            testId='automation-back'
          />
        )}
      </div>

      <div ref={stepRef} data-testid='automation-step'>
        <div key={openStepKey} className='animate-fade-in'>
          {renderStep()}
        </div>
      </div>
    </div>
  );
}
